import { ButtonV2 } from '@cimplico/components';
import { ArrowPathIcon } from '@heroicons/react/20/solid';
import { useIsActiveSubscription } from '../../../../hooks/use-is-active-subscription';
import SectionLabel from '../../../../components/section-label/SectionLabel';
import SettingsContainer from '../../../../components/settings-container/SettingsContainer';
import { User } from '../../../../types/user';
import ResetMFAConfirmDialog from './ResetMFAConfirmDialog';
import { useAuth0 } from '@auth0/auth0-react';
import { useResetMFA } from '../../../../queries/use-reset-mfa';
import { useState } from 'react';
import { useUserContext } from '../../../../providers/UserProvider';
import { useIsFirmRole } from '../../../../hooks/use-is-firm-role';
import { toastNotification } from '../../../../components/toast/Toast';

type ResetMFAProps = {
    user: User;
};

function ResetMFA({ user }: ResetMFAProps): JSX.Element {
    const activeSubscription = useIsActiveSubscription();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const loggedInUser = useUserContext();
    const { isAdmin } = useIsFirmRole();
    const adminUpdatingUser = loggedInUser.id !== user.id && isAdmin;
    const resetMFAQuery = useResetMFA(user, adminUpdatingUser);

    const isAdminResetting = isAdmin && loggedInUser.id !== user.id;

    const { logout } = useAuth0();

    function handleResetMFA(): void {
        resetMFAQuery.mutate(undefined, {
            onSuccess: () => {
                if (loggedInUser.id === user.id) {
                    logout({ logoutParams: { returnTo: window.location.origin } });
                }
                toastNotification('success', 'Successfully reset multi-factor authentication');
            },
            onError: (err) => {
                const message = err.response?.data?.message ?? 'Something went wrong';
                toastNotification('error', 'Unable to reset multi-factor authentication', message);
            },
            onSettled: () => {
                setIsDialogOpen(false);
            },
        });
        setIsDialogOpen(false);
    }

    return (
        <>
            <ResetMFAConfirmDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onReset={handleResetMFA}
                adminUpdatingUser={adminUpdatingUser}
            />
            <SettingsContainer isLastItem>
                <SectionLabel
                    title="Reset multi-factor authentication"
                    description={
                        isAdminResetting
                            ? 'Remove this user’s current configuration and require them to reinitialise their MFA settings.'
                            : 'Remove your current MFA configuration. You will be required to set this up again.'
                    }
                />
                <div className="flex items-center">
                    <ButtonV2
                        size="sm"
                        type="secondary"
                        label="Reset MFA"
                        onClick={() => setIsDialogOpen(true)}
                        isDisabled={!activeSubscription || resetMFAQuery.isLoading}
                        isLoading={resetMFAQuery.isLoading}
                        leftIcon={<ArrowPathIcon />}
                    />
                </div>
            </SettingsContainer>
        </>
    );
}

export default ResetMFA;
